export type LandingPageVariant = "webdesign" | "website-erstellen-lassen" | "webdesigner";

export type CityProfile = {
  city: string;
  slug: string;
  regionLabel: string;
  audienceSummary: string;
  localChallenge: string;
  localApproach: string;
  nearbyAreas: string[];
  typicalClients: string[];
  localSignals: string[];
};

export type CityLandingRoute = {
  city: string;
  citySlug: string;
  variant: LandingPageVariant;
};

export const cityProfiles: Record<string, CityProfile> = {
  dresden: {
    city: "Dresden",
    slug: "dresden",
    regionLabel: "Dresden und Ostsachsen",
    audienceSummary:
      "Vor allem für B2B-Dienstleister, Kanzleien, Praxen, technische Unternehmen und wachsende Mittelständler.",
    localChallenge:
      "In Dresden reicht eine hübsche Website oft nicht aus. Wer in einem kompetitiven Umfeld Leistungen erklärt, Vertrauen aufbaut und qualifizierte Anfragen will, braucht klare Inhalte und einen seriösen Auftritt.",
    localApproach:
      "Wir strukturieren Angebote so, dass Entscheider schnell verstehen, was Sie tun, für wen Sie arbeiten und wie eine Anfrage abläuft.",
    nearbyAreas: ["Radebeul", "Freital", "Heidenau", "Pirna"],
    typicalClients: [
      "B2B-Dienstleister mit erklärungsbedürftigem Angebot",
      "Ingenieur- und Technikunternehmen",
      "Kanzleien, Praxen und beratende Berufe",
    ],
    localSignals: [
      "klare Leistungsseiten statt allgemeiner Werbetexte",
      "saubere Kontaktwege für Erstgespräche und Rückfragen",
      "seriöse Darstellung für Anfragen aus Dresden und Umgebung",
    ],
  },
  senftenberg: {
    city: "Senftenberg",
    slug: "senftenberg",
    regionLabel: "Senftenberg, Lausitz und Umgebung",
    audienceSummary:
      "Geeignet für regionale Dienstleister, Handwerksbetriebe, Praxen und Unternehmen mit starkem Einzugsgebiet in der Lausitz.",
    localChallenge:
      "In Senftenberg suchen viele Interessenten nicht nach einer kreativen Agenturidee, sondern nach einem verlässlichen Anbieter mit klaren Informationen, Erreichbarkeit und nachvollziehbarem Angebot.",
    localApproach:
      "Deshalb setzen wir auf verständliche Leistungsdarstellung, einfache Anfragen und eine Website, die auch im Tagesgeschäft unkompliziert gepflegt werden kann.",
    nearbyAreas: ["Großräschen", "Lauchhammer", "Schipkau", "Schwarzheide"],
    typicalClients: [
      "Handwerksbetriebe mit regionalem Einzugsgebiet",
      "Dienstleister mit lokalem Empfehlungsmarketing",
      "Praxen und kleinere Unternehmen ohne eigene IT-Ressourcen",
    ],
    localSignals: [
      "klare Informationen zu Leistungen und Einzugsgebiet",
      "mobile Nutzbarkeit für schnelle Kontaktaufnahmen",
      "vertrauenswürdiger Auftritt für regionale Empfehlungen",
    ],
  },
  ruhland: {
    city: "Ruhland",
    slug: "ruhland",
    regionLabel: "Ruhland und südliches Brandenburg",
    audienceSummary:
      "Sinnvoll für kleine Unternehmen, die online professionell auftreten möchten, ohne eine große Agenturstruktur zu bezahlen.",
    localChallenge:
      "Gerade in kleineren Orten zählt oft die Mischung aus persönlicher Erreichbarkeit, glaubwürdigem Auftreten und einer Website, die auch bei Empfehlungen sofort Vertrauen schafft.",
    localApproach:
      "Wir halten den Prozess bewusst schlank und fokussieren die Inhalte auf Leistungen, regionale Relevanz und konkrete Kontaktmöglichkeiten.",
    nearbyAreas: ["Schwarzheide", "Lauchhammer", "Ortrand", "Senftenberg"],
    typicalClients: [
      "lokale Dienstleister und Fachbetriebe",
      "kleine B2B-Anbieter im regionalen Umfeld",
      "Unternehmen mit Website-Bedarf ohne internes Marketingteam",
    ],
    localSignals: [
      "kompakte Seitenstruktur mit klarem Nutzerversprechen",
      "weniger Agentursprech, mehr konkrete Leistungen",
      "saubere Kontakt- und Anfragewege für regionale Kunden",
    ],
  },
  schwarzheide: {
    city: "Schwarzheide",
    slug: "schwarzheide",
    regionLabel: "Schwarzheide, Lausitz und Industrieumfeld",
    audienceSummary:
      "Passend für technische Dienstleister, industrielle Zulieferer, Handwerk und Unternehmen mit professionellem B2B-Fokus.",
    localChallenge:
      "In Schwarzheide und Umgebung müssen Websites häufig nüchtern, belastbar und verständlich sein. Entscheider wollen schnell sehen, ob Angebot, Kompetenz und Erreichbarkeit zusammenpassen.",
    localApproach:
      "Deshalb gestalten wir Seiten mit klarer Struktur, nachvollziehbaren Leistungen und einer Darstellung, die seriös wirkt statt laut zu werben.",
    nearbyAreas: ["Ruhland", "Senftenberg", "Lauchhammer", "Großräschen"],
    typicalClients: [
      "technische Dienstleister und Zulieferer",
      "B2B-Unternehmen mit erklärungsbedürftigem Angebot",
      "Betriebe, die Website und Wartung nicht intern stemmen wollen",
    ],
    localSignals: [
      "präzise Leistungsdarstellung für B2B-Anfragen",
      "vertrauenswürdiger Auftritt für technische Anbieter",
      "laufende Betreuung statt einmaliger Übergabe",
    ],
  },
  hoyerswerda: {
    city: "Hoyerswerda",
    slug: "hoyerswerda",
    regionLabel: "Hoyerswerda und nördliche Lausitz",
    audienceSummary:
      "Geeignet für Handwerk, Beratung, Gesundheit, Bildung und lokale Dienstleistungsunternehmen mit regionalem Fokus.",
    localChallenge:
      "Viele Unternehmen in Hoyerswerda leben von Vertrauen, Erreichbarkeit und Empfehlungen. Eine Website muss deshalb vor allem verständlich, aktuell und glaubwürdig sein.",
    localApproach:
      "Wir konzentrieren uns auf klare Inhalte, kurze Wege zur Anfrage und eine Website, die ohne technisches Chaos zuverlässig betrieben wird.",
    nearbyAreas: ["Bernsdorf", "Lauta", "Wittichenau", "Senftenberg"],
    typicalClients: [
      "Praxen, Beratungen und personenbezogene Dienstleistungen",
      "Handwerks- und Servicebetriebe mit regionaler Kundschaft",
      "Organisationen mit Bedarf an klarer Kommunikation",
    ],
    localSignals: [
      "deutliche Botschaften statt überladener Seiten",
      "vertrauensbildende Struktur für Erstkontakte",
      "verlässliche Wartung und laufende Aktualisierung",
    ],
  },
  meissen: {
    city: "Meißen",
    slug: "meissen",
    regionLabel: "Meißen, Elbtal und Umgebung",
    audienceSummary:
      "Besonders interessant für Handwerk, Praxen, Kanzleien, regionale Marken und Unternehmen mit hohem Vertrauensbedarf.",
    localChallenge:
      "In Meißen braucht eine gute Website oft die Balance aus professioneller Außenwirkung, regionaler Verankerung und einer klaren Darstellung des Angebots.",
    localApproach:
      "Wir kombinieren eine seriöse Optik mit klarer Seitenlogik, damit Leistungen schnell verstanden werden und die Kontaktaufnahme einfach bleibt.",
    nearbyAreas: ["Coswig", "Radebeul", "Riesa", "Dresden"],
    typicalClients: [
      "Unternehmen mit starkem regionalem Vertrauensfaktor",
      "Praxen, Kanzleien und beratende Berufe",
      "Handwerks- und Dienstleistungsbetriebe mit Empfehlungsnetzwerk",
    ],
    localSignals: [
      "seriöse Außendarstellung für regionale Zielgruppen",
      "klare Angebotskommunikation statt bloßer Imagefläche",
      "einfacher Betrieb mit Hosting und Wartung aus einer Hand",
    ],
  },
};

export const webdesignCityPages: CityLandingRoute[] = [
  { city: "Dresden", citySlug: "dresden", variant: "webdesign" },
  { city: "Senftenberg", citySlug: "senftenberg", variant: "webdesign" },
  { city: "Ruhland", citySlug: "ruhland", variant: "webdesign" },
  { city: "Schwarzheide", citySlug: "schwarzheide", variant: "webdesign" },
  { city: "Hoyerswerda", citySlug: "hoyerswerda", variant: "webdesign" },
  { city: "Meißen", citySlug: "meissen", variant: "webdesign" },
];

export const websiteCreationCityPages: CityLandingRoute[] = [
  { city: "Dresden", citySlug: "dresden", variant: "website-erstellen-lassen" },
];

export const webdesignerCityPages: CityLandingRoute[] = [
  { city: "Dresden", citySlug: "dresden", variant: "webdesigner" },
];

export const allCityLandingPages = [...webdesignCityPages, ...websiteCreationCityPages];
export const seoCityLandingPages = [...allCityLandingPages, ...webdesignerCityPages];

export function getCityLandingPath(citySlug: string, variant: LandingPageVariant) {
  if (variant === "website-erstellen-lassen") {
    return `/website-erstellen-lassen-${citySlug}`;
  }

  if (variant === "webdesigner") {
    return `/webdesigner-${citySlug}`;
  }

  return `/webdesign-${citySlug}`;
}

export function getCityLandingLabel({ city, variant }: CityLandingRoute) {
  if (variant === "website-erstellen-lassen") {
    return `Website erstellen lassen ${city}`;
  }

  if (variant === "webdesigner") {
    return `Webdesigner ${city}`;
  }

  return `Webdesign ${city}`;
}

export function getCityProfile(citySlug: string) {
  return cityProfiles[citySlug];
}
